import { isString } from 'remeda'
import { t } from '@lingui/core/macro'

import {
  ItemAction,
  ItemKind,
  ItemType,
} from '@jotta/grpc-js-client/fileService'
import { isWopiDocument } from '@jotta/grpc-js-client/wopiService'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import type { FileActionContext } from '@jotta/types/FileActions'
import type { PathItemObject } from '@jotta/types/Files'
import SvgAddFile from '@jotta/ui/SvgAddFile'
import SvgAddFolder from '@jotta/ui/SvgAddFolder'
import SvgArrowCounterClockwise from '@jotta/ui/SvgArrowCounterClockwise'
import SvgCheck from '@jotta/ui/SvgCheck'
import SvgClock from '@jotta/ui/SvgClock'
import SvgCopy from '@jotta/ui/SvgCopy'
import SvgDownloadSimple from '@jotta/ui/SvgDownloadSimple'
import SvgEdit from '@jotta/ui/SvgEdit'
import SvgFileDoc from '@jotta/ui/SvgFileDoc'
import SvgFileUpload from '@jotta/ui/SvgFileUpload'
import SvgFolderOpen from '@jotta/ui/SvgFolderOpen'
import SvgMicrosoftExcelLogo from '@jotta/ui/SvgMicrosoftExcelLogo'
import SvgMicrosoftPowerpointLogo from '@jotta/ui/SvgMicrosoftPowerpointLogo'
import SvgMicrosoftWordLogo from '@jotta/ui/SvgMicrosoftWordLogo'
import SvgMinusCircle from '@jotta/ui/SvgMinusCircle'
import SvgMove from '@jotta/ui/SvgMove'
import SvgShareIos from '@jotta/ui/SvgShareIos'
import SvgTrash from '@jotta/ui/SvgTrash'
import SvgUploadFolder from '@jotta/ui/SvgUploadFolder'
import { isFolderInputSupported } from '@jotta/utils/browserFeatureChecks'
import { popPath } from '@jotta/utils/pathUtils'

import { apiPathToRouteContext } from '../FileRoute/utils/apiPathToRouteContext'

type FileActionConfig = {
  action: string
  name: string
  pluralName: (count: number) => string
  icon: BrandThemeIcon
  enabledContexts: readonly FileActionContext[]
  hasAction?:
    | ItemAction
    | ((item: PathItemObject, isOwner?: boolean) => boolean)
  actions?: readonly FileActionConfig[]
  hidden?: boolean
}
const baseFileActionConfig = [
  {
    action: 'OpenDocument',
    get name() {
      return t`Open`
    },
    pluralName() {
      return this.name
    },
    icon: SvgFileDoc,
    enabledContexts: ['FOLDER_LIST'],
    hasAction: item => isWopiDocument(item.name),
    hidden: true,
  },
  {
    action: 'OpenDocument',
    get name() {
      return t`Open`
    },
    pluralName() {
      return this.name
    },
    icon: SvgFileDoc,
    enabledContexts: ['FOLDER_LIST'],
    hasAction: item => isWopiDocument(item.name),
    hidden: true,
  },
  {
    action: 'EmptyTrash',
    get name() {
      return t`Empty trash`
    },
    pluralName() {
      return this.name
    },
    icon: SvgTrash,
    enabledContexts: [],
    hasAction: item => item.path.toLowerCase() === '/trash',
    hidden: true,
  },
  {
    action: 'Download',
    get name() {
      return t`Download`
    },
    pluralName(count) {
      return t`Download ${count} files`
    },
    icon: SvgDownloadSimple,
    enabledContexts: ['FOLDER_LIST', 'BREADCRUMB', 'PUBLIC_LINKS', 'SELECTION'],
    hasAction: item =>
      !['/photos'].includes(item.path.toLowerCase()) &&
      popPath(item.path.toLowerCase()) !== '/backup',
    hidden: false,
  },
  {
    action: 'Share',
    get name() {
      return t`Share`
    },
    pluralName() {
      return this.name
    },
    icon: SvgShareIos,
    enabledContexts: ['FOLDER_LIST', 'BREADCRUMB'],
    hasAction: (item, isOwner = true) =>
      !!item.folderShareId ||
      item.actionList.some(action =>
        [ItemAction.PUBLIC_SHARE, ItemAction.PUBLIC_UNSHARE].includes(action),
      ) ||
      (!isOwner && item.kind === ItemKind.COLLABORATION_FOLDER),
    hidden: false,
  },

  {
    action: 'Copy',
    get name() {
      return t`Copy`
    },
    pluralName(count) {
      return t`Copy ${count} files`
    },
    icon: SvgCopy,
    enabledContexts: ['FOLDER_LIST', 'SELECTION'],
    hasAction: ItemAction.COPY,
    hidden: false,
  },
  {
    action: 'Move',
    get name() {
      return t`Move`
    },
    pluralName(count) {
      return t`Move ${count} files`
    },
    icon: SvgMove,
    enabledContexts: ['FOLDER_LIST', 'SELECTION'],
    hasAction: ItemAction.MOVE,
    hidden: false,
  },
  {
    action: 'Rename',
    get name() {
      return t`Rename`
    },
    pluralName() {
      return this.name
    },
    icon: SvgEdit,
    enabledContexts: ['FOLDER_LIST'],
    hasAction: ItemAction.MOVE,
    hidden: false,
  },
  {
    action: 'Revisions',
    get name() {
      return t`Previous versions`
    },
    pluralName() {
      return this.name
    },
    icon: SvgClock,
    enabledContexts: ['FOLDER_LIST'],
    hasAction: item => item.type === ItemType.FILE,
    hidden: false,
  },
  {
    action: 'GoToFolder',
    get name() {
      return t`Go to folder`
    },
    pluralName() {
      return this.name
    },
    icon: SvgFolderOpen,
    enabledContexts: ['FOLDER_LIST'],
    hasAction: item => item.isSearchResult,
    hidden: false,
  },
  {
    action: 'Delete',
    get name() {
      return t`Delete`
    },
    pluralName(count) {
      return t`Delete ${count} files`
    },
    icon: SvgTrash,
    enabledContexts: ['FOLDER_LIST', 'SELECTION'],
    hasAction: ItemAction.DELETE,
    hidden: false,
  },
  {
    action: 'DeletePermanently',
    get name() {
      return t`Permanently delete`
    },
    pluralName() {
      return this.name
    },
    icon: SvgTrash,
    enabledContexts: ['FOLDER_LIST', 'SELECTION'],
    hasAction: ItemAction.PERMANENTLY_DELETE,
    hidden: false,
  },
  {
    action: 'Restore',
    get name() {
      return t`Restore`
    },
    pluralName() {
      return this.name
    },
    icon: SvgArrowCounterClockwise,
    enabledContexts: ['FOLDER_LIST', 'SELECTION'],
    hasAction: item =>
      item.actionList.includes(ItemAction.RESTORE) &&
      apiPathToRouteContext(item.path) !== 'backup',
    hidden: false,
  },

  {
    action: 'NewDocument',
    get name() {
      return t`New document`
    },
    pluralName() {
      return this.name
    },
    icon: SvgAddFile,
    enabledContexts: ['FAB'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: false,
    actions: [
      {
        action: 'NewWordDocument',
        name: 'Microsoft Word',
        pluralName() {
          return this.name
        },
        icon: SvgMicrosoftWordLogo,
        enabledContexts: ['FAB'],
        hasAction: ItemAction.ADD_CHILD,
        hidden: false,
      },
      {
        action: 'NewExcelDocument',
        name: 'Microsoft Excel',
        pluralName() {
          return this.name
        },
        icon: SvgMicrosoftExcelLogo,
        enabledContexts: ['FAB'],
        hasAction: ItemAction.ADD_CHILD,
        hidden: false,
      },
      {
        action: 'NewPowerpointDocument',
        name: 'Microsoft PowerPoint',
        pluralName() {
          return this.name
        },
        icon: SvgMicrosoftPowerpointLogo,
        enabledContexts: ['FAB'],
        hasAction: ItemAction.ADD_CHILD,
        hidden: false,
      },
    ],
  },
  {
    action: 'NewWordDocument',
    name: 'Microsoft Word',
    pluralName() {
      return this.name
    },
    icon: SvgMicrosoftWordLogo,
    enabledContexts: ['FAB'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: true,
  },
  {
    action: 'NewExcelDocument',
    name: 'Microsoft Excel',
    pluralName() {
      return this.name
    },
    icon: SvgMicrosoftExcelLogo,
    enabledContexts: ['FAB'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: true,
  },
  {
    action: 'NewPowerpointDocument',
    name: 'Microsoft PowerPoint',
    pluralName() {
      return this.name
    },
    icon: SvgMicrosoftPowerpointLogo,
    enabledContexts: ['FAB'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: true,
  },
  {
    action: 'CreateFolder',
    get name() {
      return t`Create new folder`
    },
    pluralName() {
      return this.name
    },
    icon: SvgAddFolder,
    enabledContexts: ['FAB'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: false,
  },
  {
    action: 'UploadFiles',
    get name() {
      return t`Upload files`
    },
    pluralName() {
      return this.name
    },
    icon: SvgFileUpload,
    enabledContexts: ['FAB', 'PUBLIC_LINKS'],
    hasAction: ItemAction.ADD_CHILD,
    hidden: false,
  },
  {
    action: 'UploadFolder',
    get name() {
      return t`Upload folder`
    },
    pluralName() {
      return this.name
    },
    icon: SvgUploadFolder,
    enabledContexts: ['FAB', 'PUBLIC_LINKS'],
    hasAction: item =>
      isFolderInputSupported() &&
      item.actionList.includes(ItemAction.ADD_CHILD),
    hidden: false,
  },
  {
    action: 'AcceptPendingInvite',
    get name() {
      return t`Accept`
    },
    pluralName() {
      return this.name
    },
    icon: SvgCheck,
    enabledContexts: ['PENDING_INVITE_LISTITEM'],
    hasAction: undefined,
    hidden: false,
  },
  {
    action: 'DeclinePendingInvite',
    get name() {
      return t`Decline`
    },
    pluralName() {
      return this.name
    },
    icon: SvgMinusCircle,
    enabledContexts: ['PENDING_INVITE_LISTITEM'],
    hasAction: undefined,
    hidden: false,
  },
  {
    action: 'DeleteDevice',
    get name() {
      return t`Delete`
    },
    pluralName() {
      return this.name
    },
    icon: SvgTrash,
    enabledContexts: ['FOLDER_LIST', 'BREADCRUMB'],
    hasAction: item => {
      return [
        ItemKind.WORKSTATION,
        ItemKind.LAPTOP,
        ItemKind.MACBOOK,
        ItemKind.IMAC,
        ItemKind.MACPRO,
        ItemKind.IPHONE,
        ItemKind.IPAD,
        ItemKind.ANDROID,
        ItemKind.WINDOWS_PHONE,
        ItemKind.WINDOWS_TABLET,
        ItemKind.CLI,
      ].includes(item.kind)
    },
    hidden: false,
  },
] as const satisfies readonly FileActionConfig[]

type ActiveFileActionBaseConfig = (typeof baseFileActionConfig)[number]
type ActiveFileActionConfig = {
  [Action in ActiveFileActionBaseConfig as Action['action']]: Action
}
export type ActiveFileActionType = keyof ActiveFileActionConfig

export type ActiveFileAction<T extends ActiveFileActionType> =
  T extends 'Disabled'
    ? ActiveFileActionConfig[T] & {
        context: null
        params: [null]
      }
    : ActiveFileActionConfig[T]['enabledContexts'][number] extends 'SELECTION'
      ? ActiveFileActionConfig[T] & {
          context: FileActionContext
          params: [
            {
              path: string
            },
          ]
        }
      : ActiveFileActionConfig[T] & {
          context: FileActionContext
          params: [
            {
              paths: string[]
            },
          ]
        }

export const fileActionApiArray = baseFileActionConfig
export const selectionFileActions = baseFileActionConfig.filter(a =>
  (a.enabledContexts as readonly string[]).includes('SELECTION'),
)
export const fileActionApi: ActiveFileActionConfig = Object.fromEntries(
  baseFileActionConfig.map(item => [item.action, item]),
) as ActiveFileActionConfig
export function isActiveFileActionType(
  value: unknown,
): value is ActiveFileActionType {
  return isString(value) && value in fileActionApi
}
